<script setup lang="ts">
import { TypeOf } from "~/graphql/generated/graphql";
import { useSearchStore } from "~/composables/stores/search";

const t = useTranslator();

const props = defineProps({
  placeholder: {
    type: String,
    default: "",
  },

  sanitizeValueFunction: {
    type: Function,
    default: null,
  },

  open: {
    type: Boolean,
    default: false,
  },
});

const searchStore = useSearchStore();

const emit = defineEmits<{ (event: "close"): void }>();

const hasFocus = ref(true);
const textField = useTemplateRef("textField");

const includedTypes = [TypeOf.AssetManagementPortfolio, TypeOf.AssetManagementProperty, TypeOf.AssetManagementTenancy];

const itemSelected = () => {
  close();
};

const blur = () => textField.value?.blur();

const hide = () => {
  blur();
  hasFocus.value = false;
};

const close = () => {
  hide();
  searchStore.searchTerm = "";
  emit("close");
};
</script>

<template>
  <ChromeModal :keepMounted="false" color="--color-transparent" :class="$style.container" :visible="props.open" @click-close="close" @click-outside="close">
    <Composition>
      <template #backgrounds>
        <CompositionBackgroundCard color="--color-white" rounded />

        <CompositionBackgroundOutline color="--color-accent-300" :borderWidth="1" borderStyle="solid" />
      </template>
      <template #layouts>
        <CompositionLayoutSingle omitPadding>
          <template #container>
            <div :class="[$style.input, hasFocus && $style.focus]">
              <TextField
                ref="textField"
                colorBackground="--color-transparent"
                colorForeground="--color-primary"
                colorPlaceholder="--color-accent-300"
                colorBorder="--color-accent-300"
                :class="$style.input"
                :placeholder="t('CHROME_SEARCH_INPUT_PLACEHOLDER')"
                minWidth="100%"
                chromeSearch
                roundedTop
                autoFocus
                v-model="searchStore.searchTerm"
                @keydown.down="() => searchStore.updateSelection(1)"
                @keydown.up="() => searchStore.updateSelection(-1)"
                @keydown.enter="searchStore.selectItem"
                @keydown.esc="hide"
                @focus="() => (hasFocus = true)"
              >
                <template #iconBefore>
                  <Icon id="search" :color="hasFocus ? '--color-primary' : '--color-accent-500'" size="large" />
                </template>
              </TextField>
            </div>

            <div :class="$style.results">
              <Autocomplete
                ref="autocomplete"
                :includedTypes="includedTypes"
                :show="props.open"
                :sanitizeValueFunction="props.sanitizeValueFunction"
                @item-selected="itemSelected"
              />
            </div>
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>
  </ChromeModal>
</template>

<style module>
.container {
  display: flex;
  flex-direction: row;
  width: 40rem;
}

.topBarSearch {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: space-between;
}

.containerSearch {
  display: flex;
  align-items: center;
  overflow-y: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-color: var(--color-supportive-3-500) var(--color-supportive-3-100);
  scrollbar-width: thin;
  padding: 1rem;
}

.containerSearch::-webkit-scrollbar {
  width: 0;
  height: 1rem;
}

.containerSearch::-webkit-scrollbar-track {
  background: var(--color-supportive-3-100);
}

.containerSearch::-webkit-scrollbar-thumb {
  background-color: var(--color-supportive-3-500);
  border-radius: calc(1rem - var(--border-radius-medium));
  border: 0.25rem solid var(--color-supportive-3-100);
}

.containerSearch::-webkit-scrollbar-thumb:hover {
  border-width: 0.2rem;
}

.input {
  display: flex;
  width: 100%;
  height: 3rem;
}

.results {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1px;
}

.label {
  display: flex;
  position: relative;
  margin-left: 0.25rem;
  align-items: center;
}

.badge {
  position: absolute;
  display: flex;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 1.25rem;
  justify-content: center;
  align-items: center;
  background-color: var(--color-accent-500);
  right: -1.75rem;
}

.advanced {
  display: flex;
  padding-right: 0.25rem;
}
</style>
